import { useWalletsContext } from '@/components/providers';
import APP_CONFIG from '@/config/app';
import {
  TokenInterface,
  UnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import {
  getCachedIconUrl,
  setCachedIconUrl,
} from '@/services/IconCacheService';
import Image from 'next/image';
import React, { FC, useEffect, useMemo, useState } from 'react';
interface TokenIconProps {
  token?: TokenInterface | UnderlyingYieldBearingAssetInterface;
  tokenSymbol?: string;
  tokenAddress?: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  applyRoundedMask?: boolean;
}
const TokenIcon: FC<TokenIconProps> = React.memo(
  ({
    tokenSymbol,
    tokenAddress,
    alt,
    token,
    className,
    width = 36,
    height = 36,
    applyRoundedMask = false,
  }) => {
    const { findTokenBySymbolOrAddress } = useWalletsContext();
    const resolvedToken = useMemo(() => {
      return token || findTokenBySymbolOrAddress(tokenSymbol, tokenAddress);
    }, [token, tokenSymbol, tokenAddress, findTokenBySymbolOrAddress]);
    const resolvedSymbol = useMemo(() => {
      return resolvedToken?.symbol || tokenSymbol || '';
    }, [resolvedToken?.symbol, tokenSymbol]);
    const tokenSymbolLowercase = resolvedSymbol.toLowerCase();
    const [finalImageUrl, setFinalImageUrl] = useState<string | null>(() =>
      getCachedIconUrl(tokenSymbolLowercase),
    );
    const [isLoading, setIsLoading] = useState(!finalImageUrl);
    const potentialImagePaths = useMemo(
      () => APP_CONFIG.TOKEN_IMAGE_PATHS(tokenSymbolLowercase),
      [tokenSymbolLowercase],
    );
    const logoUri = resolvedToken?.logoUri;
    const defaultIconPath = APP_CONFIG.DEFAULT_TOKEN_ICON_IMAGE_PATH;
    useEffect(() => {
      let isCancelled = false;
      const findValidImageUrl = async () => {
        if (finalImageUrl) {
          setIsLoading(false);
          return;
        }
        if (!tokenSymbolLowercase) {
          if (!isCancelled) setFinalImageUrl(defaultIconPath);
          setIsLoading(false);
          return;
        }
        const primaryPaths = potentialImagePaths.slice(0, -1);
        if (logoUri) primaryPaths.push(logoUri);
        const genericFallback =
          potentialImagePaths[potentialImagePaths.length - 1];
        for (const path of primaryPaths) {
          if (!path) continue;
          try {
            await new Promise((resolve, reject) => {
              const img = new window.Image();
              img.src = path;
              img.onload = resolve;
              img.onerror = reject;
            });
            if (!isCancelled) {
              setFinalImageUrl(path);
              setCachedIconUrl(tokenSymbolLowercase, path);
              return;
            }
          } catch (error) {}
        }
        if (!isCancelled) {
          setFinalImageUrl(genericFallback || defaultIconPath);
        }
      };
      setIsLoading(true);
      findValidImageUrl().finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });
      return () => {
        isCancelled = true;
      };
    }, [
      tokenSymbolLowercase,
      potentialImagePaths,
      logoUri,
      defaultIconPath,
      resolvedSymbol,
      finalImageUrl,
    ]);
    return (
      <div
        className={`relative flex items-center justify-center ${
          applyRoundedMask ? 'overflow-hidden rounded-full' : ''
        } ${className}`}
        style={{ width, height }}
      >
        {isLoading || !finalImageUrl ? (
          <div
            style={{ width, height }}
            className={`bg-transparent animate-pulse ${
              applyRoundedMask ? 'rounded-full' : ''
            }`}
          />
        ) : (
          <Image
            key={finalImageUrl}
            src={finalImageUrl}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
            className="object-cover"
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.token?.symbol === nextProps.token?.symbol &&
    prevProps.token?.address === nextProps.token?.address &&
    prevProps.tokenSymbol === nextProps.tokenSymbol &&
    prevProps.tokenAddress === nextProps.tokenAddress &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.applyRoundedMask === nextProps.applyRoundedMask,
);
TokenIcon.displayName = 'TokenIcon';
export default TokenIcon;
